import { useEffect, useState } from 'react';
import { IC, Svg } from '../art/icons.js';
import { useNav } from '../lib/router.js';
import { useStore } from '../lib/store.js';
import { Scroll, TopBar } from '../components/ui.js';
import { loadInbox, relTime, tapMessage, type InboxMessage } from '../lib/inbox.js';
import { linkTarget } from '../lib/links.js';
import { openLink } from '../lib/open.js';

/** The Messages inbox: in-app cards from sends and automations. Opening it marks them seen; tapping records the tap and opens the link. */
export function Messages() {
  const s = useStore();
  const { navigate } = useNav();
  const [list, setList] = useState<InboxMessage[] | null>(null);
  useEffect(() => {
    if (!s.me) return;
    loadInbox(true).then((r) => { setList(r.messages); s.refreshMe().catch(() => undefined); }).catch(() => setList([]));
  }, [s.me?.customer.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // The link opens straight from the tap (a page outside Tikkas, such as a Google review, in the browser); the tap is
  // recorded alongside. Waiting for the server first would get a new tab blocked as a pop-up.
  const open = (m: InboxMessage) => {
    void tapMessage(m.id);
    if (m.code && !s.code) s.applyCode(m.code).catch(() => undefined);
    const to = linkTarget(m.link);
    if (to?.kind === 'external') openLink(to.url);
    else if (to) navigate(to.path);
  };
  if (!s.me) {
    return (
      <Scroll padStyle={{ gap: 18, paddingTop: 16 }}>
        <TopBar title="Messages" />
        <div className="card col" style={{ padding: 18, gap: 12 }}><b style={{ fontSize: 16 }}>Sign in to see your messages</b><p className="muted" style={{ margin: 0, fontSize: 14 }}>Offers, news and the odd thank-you land here.</p><button type="button" className="yellow" onClick={() => navigate('/join?next=/messages')}>Sign in or join</button></div>
      </Scroll>
    );
  }
  return (
    <Scroll padStyle={{ gap: 14, paddingTop: 16 }}>
      <TopBar title="Messages" />
      {list === null && <p className="muted">Loading…</p>}
      {list && list.length === 0 && <div className="card empty"><Svg html={IC.bell} /><b>Nothing yet</b><span className="small muted">Offers, news and the odd thank-you will show here. Nothing here is a bill.</span></div>}
      {list?.map((m) => (
        <button type="button" key={m.id} className={`msgcard ${m.seen ? '' : 'unseen'}`} onClick={() => open(m)}>
          <span className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}><b style={{ fontSize: 15 }}>{m.title ?? 'Tikkas'}</b><span className="small muted tnum">{relTime(m.at)}</span></span>
          <span className="soft" style={{ fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap' }}>{m.body}</span>
          {(m.cta || m.link) && <span className="link" style={{ textDecoration: 'none', fontWeight: 600, alignSelf: 'flex-start' }}>{m.cta ?? 'Open'} →</span>}
        </button>
      ))}
      <p className="small muted" style={{ margin: 0 }}>Change what we send you under Account. Order updates are never marketing.</p>
    </Scroll>
  );
}

/** The Home card: the newest message not yet seen. Tapping marks it and opens its link. */
export function MessageCard() {
  const s = useStore();
  const { navigate } = useNav();
  const [m, setM] = useState<InboxMessage | null>(null);
  const unseen = s.me?.unseenMessages ?? 0;
  useEffect(() => {
    if (!s.me || !unseen) {
      setM(null);
      return;
    }
    loadInbox(false).then((r) => setM(r.messages.find((x) => !x.seen) ?? null)).catch(() => setM(null));
  }, [s.me?.customer.id, unseen]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!m) return null;
  const open = () => {
    void tapMessage(m.id).then(() => s.refreshMe().catch(() => undefined));
    setM(null);
    if (m.code && !s.code) s.applyCode(m.code).catch(() => undefined);
    const to = linkTarget(m.link);
    if (to?.kind === 'external') openLink(to.url);
    else navigate(to?.path ?? '/messages');
  };
  return (
    <button type="button" className="msgcard home unseen" onClick={open}>
      <span className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}><span className="eyebrow" style={{ letterSpacing: 0, textTransform: 'none' }}>Message from Tikkas</span><span className="small muted tnum">{relTime(m.at)}</span></span>
      <b style={{ fontSize: 16 }}>{m.title ?? 'Tikkas'}</b>
      <span className="soft" style={{ fontSize: 14, lineHeight: 1.45 }}>{m.body}</span>
      <span className="row" style={{ justifyContent: 'space-between' }}><span className="link" style={{ textDecoration: 'none', fontWeight: 600 }}>{m.cta ?? 'Open'} →</span>{unseen > 1 && <span className="small muted">{unseen - 1} more in Messages</span>}</span>
    </button>
  );
}

/** "How was it?" for a collected order (automation 8). One to five stars, once. */
export function RateOrder({ orderId, number, onDone }: { orderId: string; number: number; onDone?: () => void }) {
  const s = useStore();
  const [stars, setStars] = useState(0);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async (n: number) => {
    setStars(n);
    setBusy(true);
    try {
      await (await import('../lib/api.js')).api(`/api/me/orders/${orderId}/rating`, { body: { stars: n } });
      setDone(true);
      s.toast(n >= 4 ? 'Thanks! Glad you liked it.' : 'Thanks for telling us. The kitchen reads every one.');
      onDone?.();
    } catch {
      s.toast('Could not save your rating');
    } finally {
      setBusy(false);
    }
  };
  if (done) return <div className="dbl"><Svg html={IC.starSm} /><span className="grow">Thanks for rating order #{number}.</span></div>;
  return (
    <div className="card col" style={{ padding: 16, gap: 10 }}>
      <b style={{ fontSize: 15 }}>How was order #{number}?</b>
      <div className="stars" role="radiogroup" aria-label="Rate your order">{[1, 2, 3, 4, 5].map((n) => <button type="button" key={n} role="radio" aria-checked={stars === n} aria-label={`${n} star${n === 1 ? '' : 's'}`} className={n <= stars ? 'on' : ''} disabled={busy} onClick={() => send(n)}><Svg html={IC.star} /></button>)}</div>
      <span className="small muted">Two seconds. The kitchen reads every one.</span>
    </div>
  );
}
